import './RequestForm.scss';
import { useMemo, useRef, useState } from 'react';
import { MdAdd, MdDelete } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button/Button';
import { DatePicker } from '@Components/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import { awaitJSON, MetricsLambdaFetch } from '@src/utils/fetch-utils';
import { DataQaResults } from '../../dataQaContract';
import { DataQaConfig } from '../../forecasting-constants';
import { narrowToScope } from '../../forecasting-utils';
import { ComboSubmitFailure, StartScalingAnalysisResponse } from '../../scalingAnalysisContract';
import { useDataQaSeries } from '../../useDataQaSeries';
import {
  Analysis,
  AnalysisType,
  AnchorStatus,
  AnchorStatusType,
  BudgetPeriod,
  Card,
  Comparison,
  DEFAULT_COMPARISON_BUDGETS,
  DEFAULT_END,
  DEFAULT_START,
  Grain,
  MAX_DEFAULT_COMPARISON_ID,
  MAX_ISO,
  NEW_COMPARISON_PCT,
  TODAY_ISO,
  Viability
} from './request-form-constants';
import {
  buildStartNewForecastPayload,
  buildStartScalingAnalysisPayload,
  buildViability,
  formatComboLabel,
  forecastViabilityForGrain,
  hasDuplicateSubmitCombo,
  isComparisonPctInvalid,
  maskCard,
  maskScalingCard,
  PairedSubmitResult,
  pairSubmitResults,
  switchTarget
} from './request-form-utils';
import { AnalysisAvailability, RequestCard } from './RequestCard';
import {
  checkMaxWeeklySpend,
  formatDollars,
  HighestWeek,
  highestWeekForCard,
  prefillMaxWeeklySpend,
  scalingUnavailableReason
} from './scaling-request-utils';

interface RequestFormProps {
  qa: DataQaResults;
  /** S3 key of the QA run `qa` was loaded from; locates its sibling series file. */
  qaS3Key?: string;
  company: string;
  /** Channels/KPIs the client saved during onboarding; narrows the menu. */
  savedScope?: DataQaConfig;
  /** Discard resets the form in place (parent remounts via key). */
  onDiscard: () => void;
  /** Opens Forecasting > Scaling Results (the success dialog's link). */
  onGoToScalingResults?: () => void;
}

// A scaling card's anchor status. A run with no series file (schema v1) or no
// full week for the card's rows can't anchor; a fetch that errored even after
// React Query's retries can be retried from the card.
const anchorStatusFor = (
  series: { isError: boolean; isSuccess: boolean; isFetching: boolean },
  hasSeriesFile: boolean,
  hasHighestWeek: boolean
): AnchorStatusType => {
  if (!hasSeriesFile) {
    return AnchorStatus.UNAVAILABLE;
  }
  // A "Try again" in flight reads as loading, not as the error it's retrying.
  if (series.isFetching && !series.isSuccess) {
    return AnchorStatus.LOADING;
  }
  if (series.isError) {
    return AnchorStatus.FAILED;
  }
  if (!series.isSuccess) {
    return AnchorStatus.LOADING;
  }
  return hasHighestWeek ? AnchorStatus.READY : AnchorStatus.UNAVAILABLE;
};

export const RequestForm = ({
  qa,
  qaS3Key,
  company,
  savedScope,
  onDiscard,
  onGoToScalingResults = () => {}
}: RequestFormProps) => {
  const navigate = useNavigate();
  const setError = useSetError();
  const [submitting, setSubmitting] = useState(false);
  const idgen = useRef(MAX_DEFAULT_COMPARISON_ID);
  const nextId = () => {
    idgen.current += 1;
    return idgen.current;
  };

  const addUnique = (arr: string[] | undefined, value: string): string[] =>
    arr?.includes(value) ? arr : [...(arr ?? []), value];

  // Menu universe from the QA run's groups, narrowed to the scope chosen on the
  // onboarding page when one was saved (QA stays the source of truth for what
  // exists; scope only filters). Falls back to the full QA universe.
  const { channels, platformsByChannel, tacticsByCP, tacticsByChannel, kpis } = useMemo(() => {
    const byChannel: Record<string, string[]> = {};
    const byCP: Record<string, string[]> = {};
    const tacticsByCh: Record<string, string[]> = {};
    const kpiSet = new Set<string>();
    qa.groups.forEach(g => {
      byChannel[g.channel] = addUnique(byChannel[g.channel], g.platform);
      byCP[`${g.channel}|||${g.platform}`] = addUnique(
        byCP[`${g.channel}|||${g.platform}`],
        g.tactic
      );
      tacticsByCh[g.channel] = addUnique(tacticsByCh[g.channel], g.tactic);
      kpiSet.add(g.kpi);
    });
    return {
      channels: narrowToScope(Object.keys(byChannel), savedScope?.channels),
      platformsByChannel: byChannel,
      tacticsByCP: byCP,
      tacticsByChannel: tacticsByCh,
      kpis: narrowToScope([...kpiSet].sort(), savedScope?.kpis)
    };
  }, [qa, savedScope]);

  const viability: Viability = useMemo(
    () => buildViability(qa, { platformsByChannel, tacticsByCP, tacticsByChannel }),
    [qa, platformsByChannel, tacticsByCP, tacticsByChannel]
  );
  const { cardInfoFor } = viability;
  // Scaling cards build their menus off the scaling ladder (incl. channel-only).
  const scalingViability: Viability = useMemo(
    () =>
      buildViability(qa, { platformsByChannel, tacticsByCP, tacticsByChannel }, Analysis.SCALING),
    [qa, platformsByChannel, tacticsByCP, tacticsByChannel]
  );
  const viabilityFor = (analysis: AnalysisType): Viability =>
    analysis === Analysis.SCALING ? scalingViability : viability;

  const [range, setRange] = useState<DateRange>({
    start: DEFAULT_START,
    end: DEFAULT_END
  });

  const firstChannel = channels[0];
  // A card's starting selection for an analysis, and whether it's runnable.
  const seedFor = (analysis: AnalysisType, kpi: string) => {
    const v = viabilityFor(analysis);
    const selection = v.defaultSelection(firstChannel, kpi);
    const ok = v.comboViableAtGrain(
      firstChannel,
      selection.platform,
      selection.tactic,
      kpi,
      selection.grain
    );
    return { selection, ok };
  };

  // Each card owns its KPI and outcome checkboxes (volume/revenue series),
  // so one request can cover several KPIs. This replaces the Figma spec's
  // separate volume/revenue KPI dropdowns — those cross-list every KPI under
  // both until KPI-type tagging lands. makeCard centralizes the shared
  // channel/platform/tactic derivation; only id/kpi/primary/outcomes/comparison
  // vary between the initial card and later ones.
  // A card starts as `analysis` when that's runnable, else as the other one
  // when that is (so "forecast if viable, else scaling").
  const makeCard = ({
    id,
    kpi,
    primary,
    comparisons,
    volume = true,
    revenue = false,
    analysis = Analysis.FORECAST
  }: {
    id: number;
    kpi: string;
    primary: string;
    comparisons: Comparison[];
    volume?: boolean;
    revenue?: boolean;
    analysis?: AnalysisType;
  }): Card => {
    const other = analysis === Analysis.SCALING ? Analysis.FORECAST : Analysis.SCALING;
    const preferred = seedFor(analysis, kpi);
    const fallback = seedFor(other, kpi);
    const resolved = preferred.ok || !fallback.ok ? analysis : other;
    const { platform, tactic, grain } = (resolved === analysis ? preferred : fallback).selection;
    return {
      id,
      channel: firstChannel,
      platform,
      tactic,
      kpi,
      grain,
      volume,
      revenue,
      forecast: resolved === Analysis.FORECAST,
      scaling: resolved === Analysis.SCALING,
      primary,
      // Clone so cards seeded from the same DEFAULT_COMPARISON_BUDGETS never
      // share array/object references (a later in-place edit would leak across).
      comparisons: comparisons.map(c => ({ ...c })),
      // '' = the 2x-highest-week default, resolved at render (see resolveMaxWeeklySpend).
      maxWeeklySpend: '',
      budgetPeriod: BudgetPeriod.WEEK
    };
  };

  // Lazy initializer — the seed (and its defaultPlatform call) runs once, not
  // on every render.
  const [cards, setCards] = useState<Card[]>(() => [
    makeCard({ id: 1, kpi: kpis[0], primary: '50000', comparisons: DEFAULT_COMPARISON_BUDGETS })
  ]);

  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedForecasts, setSubmittedForecasts] = useState<
    { label: string; forecastId: string }[]
  >([]);
  const [submittedScaling, setSubmittedScaling] = useState<
    { label: string; scalingId: string; maxWeeklySpend: number }[]
  >([]);
  const [submitFailures, setSubmitFailures] = useState<{ label: string; message: string }[]>([]);
  // What the dialog says was submitted: every selected analysis, or on a
  // partial scaling/mixed submit just the ones that started.
  const [submittedCount, setSubmittedCount] = useState(0);

  // Weekly series (finest grain) for the scaling cards' highest-week anchor,
  // fetched once the first scaling card appears.
  const anyScalingCard = cards.some(c => c.scaling);
  const seriesQuery = useDataQaSeries(qaS3Key, qa.series_file, anyScalingCard);
  const seriesData = seriesQuery.data;

  // Each scaling selection's highest full week, computed once per selection
  // (not per render or keystroke); the cache resets when the QA run or series does.
  const highestWeekFor = useMemo(() => {
    const cache = new Map<string, HighestWeek | null>();
    return (card: Card): HighestWeek | null => {
      const key = [card.channel, card.platform, card.tactic, card.kpi, card.grain].join('|||');
      if (!cache.has(key)) {
        cache.set(key, highestWeekForCard(qa, seriesData, card));
      }
      return cache.get(key) ?? null;
    };
  }, [qa, seriesData]);
  const hasSeriesFile = Boolean(qa.series_file && qaS3Key);
  const anchorStatusForCard = (card: Card): AnchorStatusType =>
    anchorStatusFor(seriesQuery, hasSeriesFile, Boolean(highestWeekFor(card)));

  // A scaling card's max weekly spend as shown, checked and sent: what the user
  // typed, else ('') the default of 2x its highest week. Resolved in the same
  // render, so the field never flashes empty, and a series that lands after the
  // card switched still fills it.
  const resolveMaxWeeklySpend = (card: Card): Card => {
    if (!card.scaling || card.maxWeeklySpend !== '') {
      return card;
    }
    const highest = highestWeekFor(card);
    return highest ? { ...card, maxWeeklySpend: prefillMaxWeeklySpend(highest) } : card;
  };
  const resolvedCards = cards.map(resolveMaxWeeklySpend);

  const patchCard = (id: number, patch: Partial<Card>) =>
    setCards(cs => cs.map(c => (c.id === id ? { ...c, ...patch } : c)));

  // A scaling card's new selection has a new highest week, so it goes back to
  // the 2x default; with no anchor for the new selection, keep what was typed.
  const reselectScalingCard = (id: number, patch: Partial<Card>) =>
    setCards(cs =>
      cs.map(c => {
        if (c.id !== id) {
          return c;
        }
        const next = { ...c, ...patch };
        return highestWeekForCard(qa, seriesData, next) ? { ...next, maxWeeklySpend: '' } : next;
      })
    );

  const removeCard = (id: number) => setCards(cs => cs.filter(c => c.id !== id));

  // Flip a card between forecast and scaling (switchTarget: keep its visible
  // picks where the new analysis runs them, else that analysis's default
  // selection). Exactly one of forecast/scaling stays true; a scaling card
  // starts on the 2x default.
  const switchAnalysis = (card: Card, analysis: AnalysisType) => {
    const target = switchTarget(viabilityFor(analysis), card);
    if (!target) {
      return;
    }
    patchCard(card.id, {
      ...target,
      forecast: analysis === Analysis.FORECAST,
      scaling: analysis === Analysis.SCALING,
      maxWeeklySpend: ''
    });
  };

  const availabilityFor = (card: Card): AnalysisAvailability => {
    const info = cardInfoFor(card.channel, card.platform, card.tactic, card.kpi);
    return {
      forecast: {
        // A forecast card keeps the per-grain gate its checkbox always used.
        ok: card.scaling
          ? switchTarget(viability, card) !== null
          : forecastViabilityForGrain(card.grain, info).viable,
        reason: `Forecast: no viable path.${
          card.scaling && card.grain === Grain.CHANNEL
            ? ' Forecast is not offered at the channel-only grain.'
            : ''
        }`
      },
      scaling: {
        ok: switchTarget(scalingViability, card) !== null,
        reason: scalingUnavailableReason(qa, card)
      }
    };
  };

  const addComparison = (id: number) =>
    setCards(cs =>
      cs.map(c =>
        c.id === id
          ? { ...c, comparisons: [...c.comparisons, { id: nextId(), pct: NEW_COMPARISON_PCT }] }
          : c
      )
    );

  const addCard = () =>
    setCards(cs => {
      const last = cs[cs.length - 1];
      return [
        ...cs,
        makeCard({
          id: nextId(),
          kpi: last?.kpi ?? kpis[0],
          primary: '25000',
          volume: last?.volume ?? true,
          revenue: last?.revenue ?? false,
          // seed default comparison rows so new cards look/behave like the first
          comparisons: DEFAULT_COMPARISON_BUDGETS,
          // A new card copies the previous card's analysis type, like its KPI.
          analysis: last?.scaling ? Analysis.SCALING : Analysis.FORECAST
        })
      ];
    });

  // Mask each card's analyses and outcomes to what's actually viable BEFORE
  // filtering, so a card whose only checked analysis (or only checked outcome)
  // went non-viable drops out of the selected set entirely rather than lingering
  // as a masked, zero-analysis combo.
  // A scaling card's menus and outcomes come from the scaling ladder, so it's
  // masked against that; forecast cards go through maskCard exactly as before.
  const maskedCards = resolvedCards.map(card =>
    card.scaling
      ? maskScalingCard(
          card,
          scalingViability.cardInfoFor(card.channel, card.platform, card.tactic, card.kpi)
        )
      : maskCard(card, cardInfoFor(card.channel, card.platform, card.tactic, card.kpi))
  );
  const selectedCards = maskedCards.filter(c => c.forecast || c.scaling);
  // Budgets are forecast-only; a scaling card's hidden comparison rows never block it.
  const overBudget = cards.some(
    c => c.forecast && c.comparisons.some(cp => isComparisonPctInvalid(cp.pct))
  );
  const anySelected = selectedCards.length > 0;
  const outcomesSelected = maskedCards.filter(card => card.volume || card.revenue);
  const missingPlatform = selectedCards.some(c => !c.platform);
  const missingTactic = selectedCards.some(c => !c.tactic);
  const missingPrimary = selectedCards.some(c => c.forecast && !(Number(c.primary) > 0));
  // A scaling card can't submit until its highest-week check can run: not while
  // the series loads or after it failed (a $5k max against a $70k week would
  // otherwise slip through). A run that simply can't anchor (UNAVAILABLE) can.
  const scalingSpendBlocked = selectedCards.some(c => {
    if (!c.scaling) {
      return false;
    }
    const anchor = anchorStatusForCard(c);
    return (
      anchor === AnchorStatus.LOADING ||
      anchor === AnchorStatus.FAILED ||
      checkMaxWeeklySpend(c.maxWeeklySpend, highestWeekFor(c)?.spend)?.level === 'error'
    );
  });
  const duplicateCombo = hasDuplicateSubmitCombo(selectedCards);
  const canSubmit =
    anySelected &&
    selectedCards.length === cards.length &&
    outcomesSelected.length === cards.length &&
    !missingPlatform &&
    !missingTactic &&
    !missingPrimary &&
    !scalingSpendBlocked &&
    !overBudget &&
    !duplicateCombo &&
    Boolean(range.start && range.end && range.start <= range.end);
  // A scaling card is one analysis (one curve job); a forecast card is one per outcome.
  const countAnalyses = (cs: Card[]) =>
    cs.reduce(
      (n, c) =>
        n + (c.scaling ? 1 : (c.forecast ? 1 : 0) * ((c.volume ? 1 : 0) + (c.revenue ? 1 : 0))),
      0
    );
  const nAnalyses = countAnalyses(selectedCards);

  const showSubmitted = ({
    count,
    forecasts,
    scaling = [],
    failures = []
  }: {
    count: number;
    forecasts: { label: string; forecastId: string }[];
    scaling?: { label: string; scalingId: string; maxWeeklySpend: number }[];
    failures?: { label: string; message: string }[];
  }) => {
    setSubmittedCount(count);
    setSubmittedForecasts(forecasts);
    setSubmittedScaling(scaling);
    setSubmitFailures(failures);
    setShowSuccess(true);
  };

  // Forecast-only request: exactly the flow on main (any failure -> error dialog).
  const submitForecastsOnly = async () => {
    const payload = buildStartNewForecastPayload(selectedCards, range, company);
    const response = await MetricsLambdaFetch('/startNewForecast', {
      method: 'POST',
      body: payload
    });
    const { forecastIds, failures } = await awaitJSON<{
      forecastIds: string[];
      failures: unknown[];
    }>(response);
    if (failures.length > 0) {
      throw new Error(
        `${failures.length} of ${forecastIds.length + failures.length} forecasts failed to submit`
      );
    }
    // Index-aligned with selectedCards only because failures is empty here —
    // the lambda drops failed combos out of forecastIds otherwise.
    showSubmitted({
      count: nAnalyses,
      forecasts: selectedCards.map((card, i) => ({
        label: formatComboLabel(card),
        forecastId: forecastIds[i]
      }))
    });
  };

  // With scaling cards: forecast cards still go to /startNewForecast (same
  // payload as main), scaling cards to /startScalingAnalysis, in parallel. What
  // started is listed in the success dialog next to what didn't; only a request
  // where nothing started is an error.
  const submitWithScaling = async () => {
    const forecastCards = selectedCards.filter(c => !c.scaling);
    const scalingCards = selectedCards.filter(c => c.scaling);
    // One call's cards paired with their ids or failures; a call that failed
    // outright (HTTP error, or every scaling combo failing) fails each card with its error.
    const submitCall = async (
      callCards: Card[],
      call: () => Promise<{ ids: string[]; failures: ComboSubmitFailure[] }>
    ): Promise<PairedSubmitResult[]> => {
      if (callCards.length === 0) {
        return [];
      }
      try {
        const { ids, failures } = await call();
        return pairSubmitResults(callCards, ids, failures);
      } catch (e) {
        return callCards.map(card => ({ card, failure: e.message }));
      }
    };
    const [forecastPairs, scalingPairs] = await Promise.all([
      submitCall(forecastCards, async () => {
        const res = await MetricsLambdaFetch('/startNewForecast', {
          method: 'POST',
          body: buildStartNewForecastPayload(forecastCards, range, company)
        });
        const { forecastIds, failures } = await awaitJSON<{
          forecastIds: string[];
          failures: ComboSubmitFailure[];
        }>(res);
        return { ids: forecastIds, failures };
      }),
      submitCall(scalingCards, async () => {
        const res = await MetricsLambdaFetch('/startScalingAnalysis', {
          method: 'POST',
          body: buildStartScalingAnalysisPayload(scalingCards, company, qa.data_qa_id)
        });
        const { scalingIds, failures } = await awaitJSON<StartScalingAnalysisResponse>(res);
        return { ids: scalingIds, failures };
      })
    ]);
    const started = [...forecastPairs, ...scalingPairs].filter(p => p.id);
    const failed = [...forecastPairs, ...scalingPairs].filter(p => !p.id);
    // A whole call that failed puts the same message on each of its cards.
    const reasons = [...new Set(failed.map(p => p.failure))].join('; ');
    if (started.length === 0) {
      const n = countAnalyses(failed.map(p => p.card));
      throw new Error(`none of the ${n} ${n === 1 ? 'analysis' : 'analyses'} started: ${reasons}`);
    }
    showSubmitted({
      count: countAnalyses(started.map(p => p.card)),
      forecasts: forecastPairs
        .filter(p => p.id)
        .map(p => ({ label: formatComboLabel(p.card), forecastId: p.id as string })),
      scaling: scalingPairs
        .filter(p => p.id)
        .map(p => ({
          label: formatComboLabel(p.card),
          scalingId: p.id as string,
          maxWeeklySpend: Number(p.card.maxWeeklySpend)
        })),
      failures: failed.map(p => ({
        label: `${formatComboLabel(p.card)} (${p.card.scaling ? 'scaling' : 'forecast'})`,
        message: p.failure ?? ''
      }))
    });
  };

  const onSubmit = async () => {
    setSubmitting(true);
    const withScaling = selectedCards.some(c => c.scaling);
    try {
      if (withScaling) {
        await submitWithScaling();
      } else {
        await submitForecastsOnly();
      }
    } catch (e) {
      setError({
        message: `Couldn't submit ${withScaling ? 'request' : 'forecast request'}: ${e.message}`,
        reportError: e
      });
    }
    setSubmitting(false);
  };

  // Defensive: the parent only mounts this on a non-empty QA run, but a saved
  // scope that filters everything out would leave nothing to seed a card with.
  if (channels.length === 0 || kpis.length === 0) {
    return <p className='req-subtitle'>No channels or KPIs are in scope for forecasting yet.</p>;
  }

  return (
    <>
      <p className='req-subtitle'>
        Only combinations Data QA found viable are offered below. Pick your scope, budgets, and
        whether to forecast or run a scaling analysis.
      </p>

      {/* ---- Shared parameters ---- */}
      <section className='req-params'>
        <div className='req-param'>
          <span className='req-label'>Forecast period</span>
          <DatePicker
            range={range}
            isOutsideRange={d => d < TODAY_ISO || d > MAX_ISO}
            maxDate={MAX_ISO}
            minDate={TODAY_ISO}
            onChange={(next: DateRange) => setRange(next)}
          />
          <span className='req-hint'>Future only, up to 3 months</span>
        </div>
      </section>

      {/* ---- Channel / Platform cards ---- */}
      {resolvedCards.map(card => (
        <RequestCard
          key={card.id}
          {...card}
          viability={card.scaling ? scalingViability : viability}
          analysisAvailability={availabilityFor(card)}
          onSwitchAnalysis={analysis => switchAnalysis(card, analysis)}
          onSelectionChange={patch => reselectScalingCard(card.id, patch)}
          highestWeek={card.scaling ? highestWeekFor(card) : null}
          anchorStatus={anchorStatusForCard(card)}
          onRetryAnchor={() => seriesQuery.refetch()}
          patchCard={patchCard}
          kpis={kpis}
          channels={channels}
          canRemove={cards.length > 1}
          onRemove={() => removeCard(card.id)}
          onAddComparison={() => addComparison(card.id)}
        />
      ))}
      <Button
        size={Size.LG}
        appearance={Appearance.OUTLINED}
        onClick={addCard}
        leadingElement={<MdAdd />}
      >
        Add another channel / platform
      </Button>

      {/* ---- Footer actions ---- */}
      <div className='req-footer'>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          className='req-discard'
          leadingElement={<MdDelete />}
          onClick={onDiscard}
        >
          Discard
        </Button>
        <div className='req-footer-right'>
          <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL}>
            Save as Draft
          </Button>
          <Button
            disabled={!canSubmit || submitting}
            loading={submitting}
            onClick={onSubmit}
            data-testid='request-forecast-submit'
          >
            Request Forecast{nAnalyses > 0 ? ` (${nAnalyses})` : ''}
          </Button>
        </div>
      </div>

      {/* Success → results. Today's dialog, plus a scaling list and link when
          a scaling card was submitted (and only that link when nothing else was). */}
      <AlertDialog
        intent={submitFailures.length > 0 ? Intent.WARNING : Intent.SUCCESS}
        show={showSuccess}
        onHide={() => setShowSuccess(false)}
        title={submitFailures.length > 0 ? 'Request partly submitted' : 'Request submitted'}
        primaryAction={
          submittedForecasts.length > 0
            ? {
                onClick: () => navigate(`/${company}/forecasting`),
                label: 'Go to Forecast Results'
              }
            : { onClick: onGoToScalingResults, label: 'Go to Scaling results' }
        }
        secondaryAction={{ onClick: () => setShowSuccess(false), label: 'Done' }}
        tertiaryAction={
          submittedForecasts.length > 0 && submittedScaling.length > 0
            ? {
                onClick: onGoToScalingResults,
                label: 'Go to Scaling results',
                appearance: Appearance.OUTLINED
              }
            : undefined
        }
        body={
          <>
            {submittedForecasts.length > 0 ? (
              <p>
                Your request for {submittedCount} {submittedCount === 1 ? 'analysis' : 'analyses'}{' '}
                has been submitted, not completed — forecasts run in the background and can take a
                while. Check back on the Forecast Results page to track status and see results as
                they finish.
              </p>
            ) : (
              <p>
                Your request for {submittedCount} scaling{' '}
                {submittedCount === 1 ? 'analysis' : 'analyses'} has been submitted, not completed.
                Scaling analyses run in the background and can take a while; you&apos;ll get an
                email when each finishes, and results appear on the Scaling Results tab.
              </p>
            )}
            <ul className='req-submitted-forecasts'>
              {submittedForecasts.map(({ label, forecastId }) => (
                <li key={forecastId}>
                  <span className='req-submitted-combo'>{label}</span>
                  <span className='req-submitted-id'>{forecastId}</span>
                </li>
              ))}
            </ul>
            {submittedScaling.length > 0 && (
              <div className='req-submitted-scaling' data-testid='submitted-scaling'>
                <span className='req-submitted-heading'>Scaling analyses</span>
                {submittedForecasts.length > 0 && (
                  <p className='req-submitted-note'>
                    Results appear on the Scaling Results tab; you&apos;ll get an email when each
                    finishes.
                  </p>
                )}
                <ul className='req-submitted-forecasts'>
                  {submittedScaling.map(({ label, scalingId, maxWeeklySpend }, i) => (
                    <li key={scalingId || i}>
                      <span className='req-submitted-combo'>
                        {label} · up to {formatDollars(maxWeeklySpend)}/wk
                      </span>
                      <span className='req-submitted-id'>{scalingId}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {submitFailures.length > 0 && (
              <div className='req-submitted-scaling' data-testid='submit-failures'>
                <span className='req-submitted-heading'>Couldn&apos;t submit</span>
                <ul className='req-submitted-forecasts'>
                  {submitFailures.map(({ label, message }) => (
                    <li key={label}>
                      <span className='req-submitted-combo'>{label}</span>
                      <span className='req-submitted-error'>{message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        }
      />
    </>
  );
};
